import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import firebaseAuthService from '../services/firebase/FirebaseAuthService';
import { AuditService } from '../services/firebase/AuditService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserRole } from '../types/roles';

/** Map team_members role string to role ID for users collection */
function teamRoleToUserRole(teamRole: string): string {
  // Check known default roles
  switch (teamRole) {
    case 'System Administrator': return 'system_admin';
    case 'Clinical Director': return 'clinical_director';
    case 'Administrative Staff': return 'administrative_staff';
    case 'Provider (Clinician)': return 'provider';
  }
  // Custom roles: normalize name to ID (e.g., "HR Manager" → "hr_manager")
  if (teamRole) {
    return teamRole.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  }
  return 'provider';
}

const ProviderLogin: React.FC = () => {
  const { user, userProfile, isProvider, isClient, roles, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Any staff role (not client) should go to provider dashboard
  const isStaff = roles.length > 0 && !roles.every(r => r === 'client');

  // Redirect to dashboard if already logged in as staff
  useEffect(() => {
    if (user && (isProvider || isStaff)) {
      navigate('/provider-dashboard', { replace: true });
    }
  }, [user, isProvider, isStaff, navigate]);

  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = (): boolean => {
    if (!credentials.username.trim()) {
      setError('Username is required');
      return false;
    }
    if (!credentials.password) {
      setError('Password is required');
      return false;
    }
    if (credentials.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Check if account is locked
      const blocked = await AuditService.isBlocked(credentials.username);
      if (blocked) {
        setError('Account temporarily locked due to too many failed attempts. Please try again in 30 minutes.');
        setIsLoading(false);
        return;
      }

      const userCredential = await firebaseAuthService.loginWithEmail(
        credentials.username,
        credentials.password,
        'provider'
      );

      // Log successful login
      AuditService.logAuthEvent(userCredential.user.uid, 'login', 'provider');

      // Update team member status to active on login + sync role to user profile
      try {
        let q = query(collection(db, 'team_members'), where('email', '==', credentials.username.toLowerCase()));
        let snap = await getDocs(q);
        // Fallback: if no match by email, the user might be the practice owner
        // whose seeded team_members doc has a placeholder email (e.g., dr.maya@mycliniccore.com)
        if (snap.empty) {
          const ownerQ = query(collection(db, 'team_members'), where('practiceOwnerId', '==', userCredential.user.uid));
          const ownerSnap = await getDocs(ownerQ);
          // Find the owner's own doc — match by authUid, or by role (System Administrator)
          // if the authUid is missing/wrong (e.g., seeded with placeholder email)
          const ownerDoc = ownerSnap.docs.find(d => {
            const data = d.data();
            return data.authUid === userCredential.user.uid;
          }) || ownerSnap.docs.find(d => {
            const data = d.data();
            return data.role === 'System Administrator' && (!data.authUid || data.authUid !== userCredential.user.uid);
          });
          if (ownerDoc) {
            // Update the email to match their actual login email
            await updateDoc(doc(db, 'team_members', ownerDoc.id), {
              email: credentials.username.toLowerCase(),
              authUid: userCredential.user.uid,
              status: 'active',
              lastSignIn: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
            });
            // Re-query so the role sync below runs
            q = query(collection(db, 'team_members'), where('email', '==', credentials.username.toLowerCase()));
            snap = await getDocs(q);
          }
        }
        for (const d of snap.docs) {
          const teamData = d.data();
          // Update team member status + link auth UID
          const teamUpdate: Record<string, any> = {
            lastSignIn: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
            authUid: userCredential.user.uid, // Link Firebase Auth UID to team member
          };
          if (teamData.status !== 'active') {
            teamUpdate.status = 'active';
          }
          await updateDoc(doc(db, 'team_members', d.id), teamUpdate);
          // Sync role from team_members → users collection
          const correctRole = teamRoleToUserRole(teamData.role);
          const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const currentRoles: string[] = userData.roles || [];
            if (!currentRoles.includes(correctRole) || currentRoles.length !== 1 || currentRoles[0] !== correctRole) {
              await updateDoc(doc(db, 'users', userCredential.user.uid), {
                roles: [correctRole],
                primaryRole: correctRole,
              });
            }
          }
        }
      } catch (e) { console.warn('Team member status/role sync skipped:', e); }

      // Refresh profile so AuthContext picks up the synced role (e.g., Clinical Director)
      await refreshProfile();

      // Signal to dashboard that this is a fresh login (not auto-auth) — enables voice greeting
      sessionStorage.setItem('clinic_fresh_login', 'true');

      navigate('/provider-dashboard');
    } catch (err: any) {
      // Log failed login attempt
      const result = await AuditService.logFailedLogin(credentials.username);
      if (result.blocked) {
        setError('Account temporarily locked due to too many failed attempts. Please try again in 30 minutes.');
      } else if (result.attemptsRemaining <= 2) {
        setError(`Login failed. ${result.attemptsRemaining} attempt(s) remaining before account lock.`);
      } else {
        setError(err.message || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!credentials.username.trim()) {
      toast.warning('Email Required', 'Please enter your email address first.');
      return;
    }

    try {
      await firebaseAuthService.resetPassword(credentials.username);
      toast.success('Reset Email Sent', `Password reset email sent to ${credentials.username}. Please check your inbox.`);
    } catch (error: any) {
      toast.error('Reset Failed', error.message || 'Failed to send password reset email.');
    }
  };

  return (
    <div className="app">
      <Header />
      
      <main id="main-content" className="page">
        <div className="container">
          <div style={{ 
            maxWidth: '420px', 
            margin: '0 auto',
            paddingTop: '3rem'
          }}>
            {/* Header */}
            <div className="text-center" style={{ marginBottom: '2rem' }}>
              <div style={{ 
                fontSize: '2rem', 
                marginBottom: '0.5rem',
                textAlign: 'center'
               
              }}>
                My ClinicCore
              </div>
              <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
                Provider Login
              </h1>
              <p style={{ color: '#d1d5db' }}>
                Secure access for healthcare providers and staff
              </p>
            </div>

            {/* Login Form */}
            <div className="card">
              <form onSubmit={handleSubmit} aria-label="Provider login form">
                {/* Username Field */}
                <div className="form-group">
                  <label htmlFor="provider-username" className="form-label">
                    Username / Email
                  </label>
                  <input
                    id="provider-username"
                    type="text"
                    className="form-input"
                    placeholder="Enter your username or email"
                    value={credentials.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    disabled={isLoading}
                    autoComplete="username"
                    required
                    aria-required="true"
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={error ? 'provider-login-error' : undefined}
                  />
                </div>

                {/* Password Field */}
                <div className="form-group">
                  <label htmlFor="provider-password" className="form-label">
                    Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="provider-password"
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      placeholder="Enter your password"
                      value={credentials.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={isLoading}
                      autoComplete="current-password"
                      required
                      aria-required="true"
                      aria-invalid={error ? 'true' : undefined}
                      aria-describedby={error ? 'provider-login-error' : undefined}
                      style={{ paddingRight: '3rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#d1d5db',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                      disabled={isLoading}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {/* Remember Me Checkbox */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '1.5rem'
                }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: '#d1d5db'
                  }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={isLoading}
                      style={{
                        marginRight: '0.5rem',
                        accentColor: '#D4A017'
                      }}
                    />
                    Remember me
                  </label>
                  
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#D4A017',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Error Message */}
                {error && (
                  <div id="provider-login-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  style={{ width: '100%', marginBottom: '1rem' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Signing In...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              {/* Security Notice */}
              <div style={{ 
                marginTop: '1.5rem',
                padding: '1rem',
                backgroundColor: '#374151',
                borderRadius: '0.5rem',
                border: '1px solid #4b5563'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ fontSize: '1rem' }}>🔒</span>
                  <h4 style={{
                    fontSize: '0.875rem',
                    color: '#D4A017',
                    margin: 0
                  }}>
                    Secure Login
                  </h4>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#d1d5db', margin: 0 }}>
                  Your connection is encrypted and secure. All sessions are protected with end-to-end encryption.
                </p>
              </div>
            </div>

            {/* Footer Links */}
            <div className="text-center" style={{ marginTop: '2rem' }}>
              {/* Self-serve registration is closed — accounts come from an
                  invitation sent by a practice administrator. Advertising a
                  "Register Here" link would send people to a dead end. */}
              <p style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
                Don't have a provider account? Ask your practice administrator
                to send you an invitation.
              </p>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Looking for patient access?{' '}
                <Link
                  to="/client-login"
                  style={{ color: '#D4A017', textDecoration: 'none' }}
                >
                  Client Portal
                </Link>
              </p>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Need help accessing your account?{' '}
                <button
                  onClick={() => toast.info('Contact Support', 'Email: support@mycliniccore.com\nPhone: 1-800-CLINIC-1')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#D4A017',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.875rem'
                  }}
                >
                  Contact Support
                </button>
              </p>
              <Link 
                to="/" 
                style={{ 
                  color: '#d1d5db', 
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  display: 'inline-block',
                  marginTop: '1rem'
                }}
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        textAlign: 'center',
        padding: '1rem',
        backgroundColor: '#16325B',
        borderTop: '1px solid #374151',
        zIndex: 1000
      }}>
        <p style={{
          color: '#d1d5db',
          fontSize: '0.875rem',
          margin: 0
        }}>
          &copy; 2026 My ClinicCore |{' '}
            <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>BAA</button> |{' '}
            <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>Terms</button> |{' '}
            <button type="button" style={{ color: '#6CA6C1', cursor: 'pointer', background: 'none', border: 'none', padding: '0.25rem', fontSize: 'inherit', textDecoration: 'underline' }}>Privacy</button>
        </p>

        {/* CPT Statement — collapsible */}
        <div style={{
          marginTop: '1rem',
          padding: '0.5rem',
          backgroundColor: '#1f2937',
          borderRadius: '0.25rem',
          border: '1px solid #374151'
        }}>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('cpt-details-provider');
              if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
            }}
            aria-expanded="false"
            aria-controls="cpt-details-provider"
            style={{
              color: '#d1d5db',
              fontSize: '0.75rem',
              margin: 0,
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              background: 'none',
              border: 'none',
              padding: 0
            }}
          >
            <span>My ClinicCore partners with the AMA to use CPT codes</span>
            <span style={{ fontSize: '0.65rem', color: '#6CA6C1', marginLeft: '8px' }}>tap to expand</span>
          </button>
          <p id="cpt-details-provider" style={{
            color: '#ffffff',
            fontSize: '0.875rem',
            margin: '0.25rem 0 0 0',
            lineHeight: '1.3',
            textAlign: 'left',
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'none'
          }}>
            CPT &copy; 2026 American Medical Association. All Rights reserved. CPT is a registered trademark of the American Medical Association. Fee schedules, relative value units, conversion factors and/or related components are not assigned by the AMA, are not part of CPT, and the AMA is not recommending their use. The AMA does not directly or indirectly practice medicine or dispense medical services. The AMA assumes no liability for data contained or not contained herein.
          </p>
        </div>
      </div>

      {/* Add loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ProviderLogin;